// Small shared pieces from prototype G: icon wrapper, avatar, journey dots (+ hover legend), badges.
import { CircleNotch, Lock, type Icon as PhIcon, Timer } from "@phosphor-icons/react";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { dur, hue, initials } from "@/lib/format";
import { windowLeft } from "@/model/cap";
import { journey, journeyLegend } from "@/model/journey";
import type { Person } from "@/model/people";
import { type PStatus, STATUS_COLOR, STATUS_LABEL } from "@/model/status";

type Weight = "regular" | "bold" | "fill";

/** Phosphor icon inside <i class="ph"> so the prototype's `X i {font-size}` rules apply. */
export function Ic({
  i: I,
  w = "regular",
  className,
  style,
}: {
  i: PhIcon;
  w?: Weight;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <i className={`ph ${className ?? ""}`} style={style} aria-hidden="true">
      <I weight={w} />
    </i>
  );
}

export function Av({ name, lg }: { name: string; lg?: boolean }) {
  return (
    <span className={`av${lg ? " lg" : ""}`} style={{ "--h": hue(name) } as CSSProperties}>
      {initials(name)}
    </span>
  );
}

function usePrev<T>(v: T): T | undefined {
  const r = useRef<T | undefined>(undefined);
  const prev = r.current;
  useEffect(() => {
    r.current = v;
  });
  return prev;
}

/** Four dots. A status change animates the newly lit dots once. */
export function JourneyDots({ s }: { s: PStatus }) {
  const prev = usePrev(s);
  const d = journey(s);
  const pd = prev && prev !== s ? journey(prev) : null;
  let k = 0;
  return (
    <span className="journey" data-j={s} aria-label={STATUS_LABEL[s]} role="img">
      {d.map((cls, i) => {
        const pop = !!pd && !!cls && cls !== pd[i];
        const style = pop ? ({ "--jd": `${k++ * 0.12}s` } as CSSProperties) : undefined;
        return <i key={i} className={`${cls}${pop ? " pop" : ""}`} style={style} />;
      })}
    </span>
  );
}

/** Journey dots with the hover legend (prototype jtip). */
export function Journey({ s }: { s: PStatus }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span style={{ display: "inline-flex" }}>
          <JourneyDots s={s} />
        </span>
      </TooltipTrigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content sideOffset={8} className="jtip rdx" collisionPadding={8}>
          <div className="jh">
            <JourneyDots s={s} />
            {STATUS_LABEL[s]}
          </div>
          <ol>
            {journeyLegend(s).map((l) => (
              <li key={l.label} className={l.cls}>
                <i />
                {l.label}
              </li>
            ))}
          </ol>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </Tooltip>
  );
}

export function StatusBadge({ s }: { s: PStatus }) {
  const prev = usePrev(s);
  return (
    <span
      className={`badge st${prev && prev !== s ? " morph" : ""}`}
      data-st={s}
      style={{ "--c": STATUS_COLOR[s] } as CSSProperties}
    >
      {s === "sending" ? <Ic i={CircleNotch} className="spin" style={{ fontSize: 12 }} /> : <span className="dot" />}
      {STATUS_LABEL[s]}
    </span>
  );
}

/** Reply-window chip: time left, amber under 3h; closed; opted out. */
export function WinChip({ p, now }: { p: Person; now: number }) {
  if (p.optedOut) return <span className="win closed">Opted out</span>;
  const l = windowLeft(p.conv, now);
  if (l <= 0)
    return (
      <span className="win closed">
        <Ic i={Lock} />
        Closed
      </span>
    );
  return (
    <span className={`win${l < 3 * 3600e3 ? " low" : ""}`}>
      <Ic i={Timer} />
      {dur(l)}
    </span>
  );
}

/** Ticks once per `ms`; for countdowns inside a component. */
export function useTick(ms: number): number {
  const [n, setN] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setN(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return n;
}
